import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from './Login';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// ─── STYLES ───────────────────────────────────────────────────────────────────

const Page = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #efeae2;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const TestBanner = styled.div`
  background: #f59e0b;
  color: #422006;
  font-size: 0.72rem;
  font-weight: 700;
  text-align: center;
  padding: 0.35rem 1rem;
  letter-spacing: 0.04em;
  padding-top: max(0.35rem, env(safe-area-inset-top));
  flex-shrink: 0;
`;

const Header = styled.div`
  background: #075e54;
  color: white;
  padding: 0.6rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.18);
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 1.4rem;
  line-height: 1;
  &:active { opacity: 0.7; }
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #128c7e;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
`;

const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name {
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .status {
    font-size: 0.72rem;
    opacity: 0.85;
    line-height: 1.2;
    margin-top: 0.1rem;
  }
`;

const ClearChatBtn = styled.button`
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.45);
  color: white;
  border-radius: 1rem;
  cursor: pointer;
  padding: 0.3rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s, transform 0.1s;
  &:active { background: rgba(255,255,255,0.25); transform: scale(0.95); }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem 0.75rem 0.5rem;
  background-image:
    linear-gradient(rgba(239,234,226,0.93), rgba(239,234,226,0.93)),
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'><circle cx='20' cy='20' r='1.2' fill='%23d1c7b8'/></svg>");
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  .emoji { font-size: 3rem; margin-bottom: 0.75rem; }
  .title { font-size: 1rem; font-weight: 600; color: #374151; margin-bottom: 0.4rem; }
  .desc  { font-size: 0.85rem; line-height: 1.5; max-width: 280px; }
`;

const Bubble = styled(motion.div)`
  max-width: 78%;
  align-self: ${p => p.$role === 'user' ? 'flex-end' : 'flex-start'};
  background: ${p => p.$role === 'user' ? '#d9fdd3' : '#ffffff'};
  color: #1f2937;
  padding: 0.5rem 0.7rem 0.45rem;
  border-radius: 0.55rem;
  border-${p => p.$role === 'user' ? 'top-right' : 'top-left'}-radius: 0;
  font-size: 0.92rem;
  line-height: 1.35;
  box-shadow: 0 1px 1px rgba(0,0,0,0.08);
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const dots = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30%           { transform: translateY(-3px); opacity: 1; }
`;

const TypingBubble = styled(motion.div)`
  align-self: flex-start;
  background: #ffffff;
  padding: 0.55rem 0.8rem;
  border-radius: 0.55rem;
  border-top-left-radius: 0;
  display: flex;
  gap: 4px;
  box-shadow: 0 1px 1px rgba(0,0,0,0.08);
  span {
    width: 7px; height: 7px;
    background: #9ca3af;
    border-radius: 50%;
    animation: ${dots} 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

const InputBar = styled.form`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
  padding: 0.5rem 0.6rem;
  padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
  background: #f0f2f5;
  flex-shrink: 0;
  border-top: 1px solid #e5e7eb;
`;

const TextInput = styled.textarea`
  flex: 1;
  border: none;
  border-radius: 1.25rem;
  background: white;
  padding: 0.6rem 0.85rem;
  font-size: 0.95rem;
  font-family: inherit;
  resize: none;
  outline: none;
  min-height: 40px;
  max-height: 120px;
  line-height: 1.35;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  &:focus { box-shadow: 0 1px 4px rgba(7,94,84,0.15); }
`;

const SendBtn = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #075e54;
  color: white;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s, transform 0.1s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:active:not(:disabled) { transform: scale(0.92); }
`;

const InitialLoader = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 0.9rem;
`;

// ─── COMPONENTE ───────────────────────────────────────────────────────────────

const SimularAtendimento = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages]  = useState([]);
  const [draft, setDraft]        = useState('');
  const [sending, setSending]    = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const scrollRef = useRef(null);
  const inputRef  = useRef(null);

  const aiName = user?.aiSettings?.assistantName || 'Assistente';
  const initial = (aiName.trim()[0] || 'A').toUpperCase();

  // Carrega histórico ao entrar
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/agent/test-conversation`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { messages: [] })
      .then(data => setMessages(data.messages || []))
      .catch(() => {})
      .finally(() => setLoadingInitial(false));
  }, [token]);

  // Auto-scroll para o fim quando entra mensagem ou aparece o "digitando"
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setDraft('');
    setSending(true);

    try {
      const r = await fetch(`${API}/agent/test-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao chamar a IA');
      }
      const { reply } = await r.json();
      if (reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao enviar mensagem');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const clear = async () => {
    if (!window.confirm('Limpar essa conversa de teste? Não dá pra desfazer.')) return;
    try {
      await fetch(`${API}/agent/test-conversation`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages([]);
      toast.success('Conversa limpa');
    } catch {
      toast.error('Erro ao limpar');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Page>
      <TestBanner>⚠ MODO TESTE — agendamentos não são salvos</TestBanner>

      <Header>
        <BackBtn onClick={() => navigate('/configuracoes')} aria-label="Voltar">‹</BackBtn>
        <Avatar>{initial}</Avatar>
        <HeaderInfo>
          <div className="name">{aiName}</div>
          <div className="status">online · simulação</div>
        </HeaderInfo>
        <ClearChatBtn onClick={clear} aria-label="Limpar conversa" title="Limpar conversa">Limpar Chat</ClearChatBtn>
      </Header>

      <Messages ref={scrollRef}>
        {loadingInitial ? (
          <InitialLoader>Carregando…</InitialLoader>
        ) : messages.length === 0 ? (
          <EmptyState>
            <div className="emoji">💬</div>
            <div className="title">Converse com {aiName}</div>
            <div className="desc">
              Mande uma mensagem como se você fosse uma cliente.
              A IA usa seus serviços e horários reais, mas <b>nenhum agendamento é salvo</b>.
            </div>
          </EmptyState>
        ) : (
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <Bubble
                key={i}
                $role={m.role}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
              >
                {m.content}
              </Bubble>
            ))}
            {sending && (
              <TypingBubble
                key="typing"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span /><span /><span />
              </TypingBubble>
            )}
          </AnimatePresence>
        )}
      </Messages>

      <InputBar onSubmit={send}>
        <TextInput
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Digite uma mensagem"
          rows={1}
          disabled={sending}
        />
        <SendBtn type="submit" disabled={!draft.trim() || sending} aria-label="Enviar">
          ➤
        </SendBtn>
      </InputBar>
    </Page>
  );
};

export default SimularAtendimento;
